import { useMemo } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { getVcnManagementCardList } from '../../../api/vcn-management-api-client'
import type { VcnCardListSort, VcnManagementCardFilters } from '../types'

export const VCN_MANAGEMENT_CARD_QUERY_KEY = 'vcn-management-cards'

/**
 * Card Mode's list. A separate query key from the benefit list on purpose: the
 * two tabs render different entities, so one invalidation should not throw away
 * the other's cache, and their filter shapes differ.
 */
export function useVcnManagementCardList(
  filters: VcnManagementCardFilters,
  page: number,
  pageSize: number,
  activeSort?: VcnCardListSort | null
) {
  const request = useMemo(
    () => ({ ...filters, page, pageSize, ...(activeSort ?? {}) }),
    [filters, page, pageSize, activeSort]
  )

  const requestKey = useMemo(() => JSON.stringify(request), [request])

  return useQuery({
    queryKey: [VCN_MANAGEMENT_CARD_QUERY_KEY, requestKey],
    queryFn: () => getVcnManagementCardList(request),
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    // Paging changes the query key, so without this react-query treats each
    // page as a brand-new query and `data` goes undefined mid-flight — the
    // table would vanish and come back. Keeping the previous page's data lets
    // the grid stay put and dim while the next page loads.
    placeholderData: keepPreviousData,
  })
}
